import { ApiError, isStepUpError } from '@truecare/api-client';

/**
 * H-F2 — inline error message for a failed destructive/status-transition
 * mutation, or `null` when the failure is a TOTP step-up.
 *
 * TOTP step-up errors are intercepted + replayed by the global
 * MutationCache → TotpStepUpModalProvider bridge (main.tsx); surfacing
 * them inline would show a stale alert on an action that is about to
 * succeed on replay. Callers use the `null` return to mean "do not show
 * an error, and do not clear the input" — the replayed success
 * re-renders the card from fresh server state.
 *
 * The 409 branch is the multi-operator race the audit called out: two
 * operators acting on the same entity, the second gets a stale-state
 * conflict.
 */
export function actionErrorMessage(e: unknown): string | null {
  if (isStepUpError(e)) return null;
  if (e instanceof ApiError) {
    // Prefer the backend's Convention-03 `message` when present (audit H6). A
    // 409 like `missing_requirements` carries an ACTIONABLE message naming the
    // outstanding items; blindly returning the generic race copy below dropped
    // it, so an operator approving an incomplete caregiver saw a misleading
    // "another operator may have acted — refresh" on every retry and never
    // learned which requirement blocked approval.
    const body =
      e.body && typeof e.body === 'object'
        ? (e.body as { message?: unknown; errors?: unknown })
        : null;
    const bodyMessage = typeof body?.message === 'string' ? body.message.trim() : '';

    if (e.status === 409) {
      if (bodyMessage) return bodyMessage;
      return 'This changed on the server — another operator may have just acted. Refresh and try again.';
    }
    if (e.status >= 500) {
      return `The server had a problem (${String(e.status)}). Nothing was changed — try again.`;
    }
    // 4xx (esp. a server-side "Validation failed"): if the backend returned
    // per-field Zod issues, NAME the field(s) so the operator isn't stuck on a
    // generic message. Client-side forms already show inline errors; this covers
    // the case where a request reaches the server and Zod rejects it there.
    const fieldDetail = formatZodIssues(body?.errors);
    const base = bodyMessage || e.message || `Action failed (${String(e.status)}).`;
    return fieldDetail ? `${base} — ${fieldDetail}` : base;
  }
  return e instanceof Error ? e.message : 'Action failed. Please try again.';
}

/**
 * nestjs-zod validation errors arrive as `body.errors`: an array of
 * `{ path: (string|number)[], message: string }`. Flatten them into a compact,
 * deduped `field: message; field: message` string so a "Validation failed"
 * names exactly which field(s) failed. Returns '' when there's nothing usable.
 */
function formatZodIssues(errors: unknown): string {
  if (!Array.isArray(errors)) return '';
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const issue of errors) {
    if (!issue || typeof issue !== 'object') continue;
    const { path, message } = issue as { path?: unknown; message?: unknown };
    if (typeof message !== 'string' || message.length === 0) continue;
    const field = Array.isArray(path) && path.length > 0 ? path.join('.') : '';
    const line = field ? `${field}: ${message}` : message;
    if (seen.has(line)) continue;
    seen.add(line);
    parts.push(line);
  }
  return parts.join('; ');
}
